import { Key, Logout } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CountDownAnimation } from '~/components/CountDownAnimation';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { useChangePasswordForm } from '~/features/ChangePassword/ChangePasswordForm/useChangePasswordForm';
import { useSxStyles } from '~/features/ChangePassword/ChangePasswordForm/useStyles';

type PropsType = {
  username: string;
  onClose?: () => void;
  onUpdate: () => void;
  description?: ReactNode;
};

export const ChangePasswordForm = (props: PropsType) => {
  const { onClose, onUpdate, description, username } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    form: { control },
    onSave,
    submissionResponse,
  } = useChangePasswordForm({ onUpdate, username });

  if (!!submissionResponse?.fLoginPwChangeSuccess) {
    return (
      <>
        <SolaForceDialogTitle icon={<Key />}>
          {t('laChangePassword')}
        </SolaForceDialogTitle>
        <DialogContent dividers={true}>
          <Alert severity={'success'}>
            <Box sx={sxStyles.successMessage}>
              <Typography>{t('laYPassSuccessChanged')}</Typography>
              <Typography>{t('laSigninNewPassword')}</Typography>
            </Box>
          </Alert>
          <CountDownAnimation duration={3000} onCountDownEnd={onUpdate} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onUpdate} endIcon={<Logout />}>
            {t('logout')}
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <SolaForceDialogTitle icon={<Key />} onClose={onClose}>
        {t('laChangePassword')}
      </SolaForceDialogTitle>
      <DialogContent dividers={true}>
        {!!description && <Box sx={sxStyles.description}>{description}</Box>}

        <Box component={'form'} sx={sxStyles.fieldLayout} autoComplete={'off'}>
          <Controller
            control={control}
            name={'currentPassword'}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  {...field}
                  required={true}
                  type={'password'}
                  label={t('laCurPass')}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  error={!!error}
                  helperText={error?.message}
                  size={'small'}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'newPassword'}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  {...field}
                  required={true}
                  type={'password'}
                  label={t('laNewPass')}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  placeholder={t('la6Chara')}
                  error={!!error}
                  helperText={error?.message}
                  size={'small'}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={'confirmPassword'}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  {...field}
                  required={true}
                  type={'password'}
                  label={t('laConfirmPass')}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  placeholder={t('la6Chara')}
                  error={!!error}
                  helperText={error?.message}
                  size={'small'}
                />
              );
            }}
          />

          {!!submissionResponse &&
            !submissionResponse.fLoginPwChangeSuccess && (
              <Alert severity={'error'}>{t('laCurPassIsWrong')}</Alert>
            )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSave}>{t('save')}</Button>
      </DialogActions>
    </>
  );
};
